import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Calendar, 
  CheckCircle2, 
  Circle, 
  Clock, 
  Flag, 
  Loader2, 
  MoreHorizontal, 
  Plus, 
  User,
  XCircle,
  Edit,
  Trash2
} from 'lucide-react';
import { format, isAfter, isBefore, startOfDay } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Task, TaskStatus, TaskPriority, TaskFilters } from '@/app/types/task';
import { 
  listTasks, 
  getEntityTasks, 
  markTaskComplete, 
  markTaskOpen,
  markTaskCancelled,
  deleteTask 
} from '@/app/services/taskService';
import { cn } from '@/lib/utils';

interface TaskListProps {
  entityType?: string;
  entityId?: number;
  filters?: TaskFilters;
  showFilters?: boolean;
  showCreateButton?: boolean;
  onCreateClick?: () => void;
  onTaskClick?: (task: Task) => void;
  onTaskEdit?: (task: Task) => void;
  viewMode?: 'table' | 'cards';
  maxHeight?: string;
  className?: string;
}

const priorityConfig = {
  [TaskPriority.HIGH]: { label: 'High', color: 'text-red-600', bgColor: 'bg-red-50', icon: Flag },
  [TaskPriority.MEDIUM]: { label: 'Medium', color: 'text-yellow-600', bgColor: 'bg-yellow-50', icon: Flag },
  [TaskPriority.LOW]: { label: 'Low', color: 'text-blue-600', bgColor: 'bg-blue-50', icon: Flag },
};

const statusConfig = {
  [TaskStatus.OPEN]: { label: 'Open', color: 'text-gray-600', icon: Circle },
  [TaskStatus.COMPLETED]: { label: 'Completed', color: 'text-green-600', icon: CheckCircle2 },
  [TaskStatus.CANCELLED]: { label: 'Cancelled', color: 'text-gray-400', icon: XCircle },
};

export default function TaskList({
  entityType,
  entityId,
  filters = {},
  showFilters = false,
  showCreateButton = true,
  onCreateClick,
  onTaskClick,
  onTaskEdit,
  viewMode = 'table',
  maxHeight,
  className,
}: TaskListProps) {
  const queryClient = useQueryClient();
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);

  const queryKey = entityType && entityId 
    ? ['tasks', 'entity', entityType, entityId]
    : ['tasks', 'list', filters];

  const { data: tasksData, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (entityType && entityId) {
        const tasks = await getEntityTasks(entityType, entityId);
        return { items: tasks, total: tasks.length };
      } else {
        return await listTasks({ ...filters, limit: 100 });
      }
    },
    refetchInterval: 30000,
  });

  const completeMutation = useMutation({
    mutationFn: (taskId: number) => markTaskComplete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Task marked as completed');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to complete task');
    },
  });

  const reopenMutation = useMutation({
    mutationFn: (taskId: number) => markTaskOpen(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Task reopened');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to reopen task');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (taskId: number) => markTaskCancelled(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Task cancelled');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to cancel task');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (taskId: number) => deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Task deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    },
  });

  const handleToggleComplete = (task: Task) => {
    if (task.status === TaskStatus.COMPLETED) {
      reopenMutation.mutate(task.id);
    } else {
      completeMutation.mutate(task.id);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && tasksData?.items) {
      setSelectedTasks(tasksData.items.map(t => t.id));
    } else {
      setSelectedTasks([]);
    }
  };

  const handleSelectTask = (taskId: number, checked: boolean) => {
    if (checked) {
      setSelectedTasks([...selectedTasks, taskId]);
    } else {
      setSelectedTasks(selectedTasks.filter(id => id !== taskId));
    }
  };

  const isOverdue = (task: Task) => {
    if (!task.due_date || task.status === TaskStatus.COMPLETED) return false;
    return isBefore(new Date(task.due_date), startOfDay(new Date()));
  };

  const isDueSoon = (task: Task) => {
    if (!task.due_date || task.status === TaskStatus.COMPLETED) return false;
    const dueDate = new Date(task.due_date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return isAfter(dueDate, startOfDay(today)) && isBefore(dueDate, startOfDay(tomorrow));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load tasks. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  const tasks = tasksData?.items || [];

  if (tasks.length === 0 && !showCreateButton) {
    return (
      <Alert>
        <AlertDescription>
          No tasks found.
        </AlertDescription>
      </Alert>
    );
  }

  const renderTableView = () => (
    <div className={cn("rounded-md border", maxHeight && "overflow-y-auto")} style={{ maxHeight }}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox
                checked={selectedTasks.length === tasks.length && tasks.length > 0}
                onCheckedChange={handleSelectAll}
              />
            </TableHead>
            <TableHead className="w-12"></TableHead>
            <TableHead>Task</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead>Assigned To</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tasks.map((task) => {
            const StatusIcon = statusConfig[task.status].icon;
            const PriorityIcon = priorityConfig[task.priority].icon;
            const overdue = isOverdue(task);
            const dueSoon = isDueSoon(task);

            return (
              <TableRow key={task.id} className={cn(task.status === TaskStatus.COMPLETED && "opacity-60")}>
                <TableCell>
                  <Checkbox
                    checked={selectedTasks.includes(task.id)}
                    onCheckedChange={(checked) => handleSelectTask(task.id, checked as boolean)}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => handleToggleComplete(task)}
                  >
                    <StatusIcon 
                      className={cn(
                        "h-5 w-5",
                        statusConfig[task.status].color
                      )} 
                    />
                  </Button>
                </TableCell>
                <TableCell>
                  <div 
                    className="space-y-1 cursor-pointer hover:underline"
                    onClick={() => onTaskClick?.(task)}
                  >
                    <div className="font-medium">{task.title}</div>
                    {task.description && (
                      <div className="text-sm text-muted-foreground line-clamp-1">
                        {task.description}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge 
                    variant="outline" 
                    className={cn(
                      priorityConfig[task.priority].bgColor,
                      priorityConfig[task.priority].color
                    )}
                  >
                    <PriorityIcon className="h-3 w-3 mr-1" />
                    {priorityConfig[task.priority].label}
                  </Badge>
                </TableCell>
                <TableCell>
                  {task.task_category && (
                    <Badge variant="secondary">{task.task_category}</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {task.due_date && (
                    <div className={cn(
                      "flex items-center gap-1 text-sm",
                      overdue && "text-red-600 font-medium",
                      dueSoon && "text-yellow-600 font-medium"
                    )}>
                      <Calendar className="h-3 w-3" />
                      {format(new Date(task.due_date), 'MMM dd, yyyy')}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {task.assigned_to && (
                    <div className="flex items-center gap-1 text-sm">
                      <User className="h-3 w-3" />
                      {task.assigned_to.full_name || task.assigned_to.username}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onTaskEdit?.(task)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      {task.status === TaskStatus.OPEN && (
                        <>
                          <DropdownMenuItem onClick={() => completeMutation.mutate(task.id)}>
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                            Mark Complete
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => cancelMutation.mutate(task.id)}>
                            <XCircle className="mr-2 h-4 w-4" />
                            Cancel
                          </DropdownMenuItem>
                        </>
                      )}
                      {task.status === TaskStatus.COMPLETED && (
                        <DropdownMenuItem onClick={() => reopenMutation.mutate(task.id)}>
                          <Circle className="mr-2 h-4 w-4" />
                          Reopen
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => deleteMutation.mutate(task.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  const renderCardView = () => (
    <div className={cn("grid gap-4 md:grid-cols-2 lg:grid-cols-3", maxHeight && "overflow-y-auto")} style={{ maxHeight }}>
      {tasks.map((task) => {
        const StatusIcon = statusConfig[task.status].icon;
        const PriorityIcon = priorityConfig[task.priority].icon;
        const overdue = isOverdue(task);
        const dueSoon = isDueSoon(task);

        return (
          <Card 
            key={task.id} 
            className={cn(
              "cursor-pointer hover:shadow-md transition-shadow",
              task.status === TaskStatus.COMPLETED && "opacity-60"
            )}
            onClick={() => onTaskClick?.(task)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleComplete(task);
                    }}
                  >
                    <StatusIcon 
                      className={cn(
                        "h-5 w-5",
                        statusConfig[task.status].color
                      )} 
                    />
                  </Button>
                  <Badge 
                    variant="outline" 
                    className={cn(
                      priorityConfig[task.priority].bgColor,
                      priorityConfig[task.priority].color,
                      "text-xs"
                    )}
                  >
                    <PriorityIcon className="h-3 w-3 mr-1" />
                    {priorityConfig[task.priority].label}
                  </Badge>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => {
                      e.stopPropagation();
                      onTaskEdit?.(task);
                    }}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMutation.mutate(task.id);
                      }}
                      className="text-red-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <h4 className="font-medium line-clamp-1">{task.title}</h4>
                {task.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                    {task.description}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2 text-sm">
                {task.task_category && (
                  <Badge variant="secondary" className="text-xs">
                    {task.task_category}
                  </Badge>
                )}
                {task.due_date && (
                  <div className={cn(
                    "flex items-center gap-1",
                    overdue && "text-red-600 font-medium",
                    dueSoon && "text-yellow-600 font-medium"
                  )}>
                    <Calendar className="h-3 w-3" />
                    {format(new Date(task.due_date), 'MMM dd')}
                  </div>
                )}
                {task.assigned_to && (
                  <div className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {task.assigned_to.full_name || task.assigned_to.username}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  return (
    <div className={cn("space-y-4", className)}>
      {(showCreateButton || selectedTasks.length > 0) && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {selectedTasks.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {selectedTasks.length} task{selectedTasks.length > 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          {showCreateButton && (
            <Button onClick={onCreateClick} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              New Task
            </Button>
          )}
        </div>
      )}

      {tasks.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Circle className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No tasks yet</p>
            {showCreateButton && (
              <Button onClick={onCreateClick}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Task
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? renderTableView() : renderCardView()}
    </div>
  );
}